import React, { useEffect, useRef, useState } from "react";
import { Box, Container, SxProps, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslations } from "hooks/useTranslations";
import {
  dateStringToDateObj,
  IItinerary,
  useConfig,
  CtaDialog,
} from "@thekilroygroup/shared-ui";
import { ResponseIconMap } from "components/ResponsePopup/ResponseIconMap";

import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";

import { useSelector } from "react-redux";
import { useLazyFetchFlightsForMonthQuery } from "redux/services/flightSearch";
import { ISearchForm, searchFormSelector } from "redux/searchForm";
import { sxStyles } from "../sharedStyles";
import { oneWayStyles } from "./oneWayStyles";
import {
  localeMap,
  LocaleKey,
} from "components/DatePickers/locales.interfaces";
import { HeadRow } from "./HeadRow";
import { Caption } from "./Caption";
import { CalendarButtonOneWay } from "./CalendarButtonOneWay";
import { format, getMonth } from "date-fns";
import { buildQueryString } from "utils/queryStrings";
import useQueryParams, { IQueryParams } from "hooks/useQueryParams";

interface IProps {
  setSelectedItinerary: (itinerary: IItinerary) => void;
  selectedItinerary?: IItinerary | null;
  showYouthStudentItineraries: boolean;
}

export const OneWayCalendar = (props: IProps) => {
  const [showErrorPopup, setShowErrorPopup] = useState<boolean>(false);
  const { showYouthStudentItineraries } = props;
  const theme = useTheme();
  const sx = sxStyles;
  const { locale, appConfig } = useConfig();
  const searchForm: ISearchForm = useSelector(searchFormSelector);
  const { searchQuery }: IQueryParams = useQueryParams();
  const navigate = useNavigate();
  const navigateToSearchPage = () => {
    navigate(`/search-results?${buildQueryString(searchForm)}`);
  };
  const [searchedMonths, setSearchedMonths] = useState<number[]>([]);
  const [allItineraries, setAllItineraries] = useState<
    Record<string, IItinerary>
  >({});
  const [filteredItineraries, setFilteredItineraries] = useState<
    Record<string, IItinerary>
  >({});
  const [lowestPriceByMonth, setLowestPriceByMonth] = useState<
    Record<string, number>
  >({});

  // Persist last displayed price per date across mounts
  const priceMemoryRef = useRef<Record<string, number>>({});

  const [triggerForMonth, resultForMonth] = useLazyFetchFlightsForMonthQuery();
  const { translations } = useTranslations();
  const { data, isFetching, isError } = resultForMonth;
  useEffect(() => {
    if (isError) {
      setShowErrorPopup(true);
    }
  }, [isFetching]);

  useEffect(() => {
    const currentMonth = getMonth(
      dateStringToDateObj(searchForm.flights[0].departureDate) as Date,
    );
    setSearchedMonths([...searchedMonths, currentMonth]);
    triggerForMonth({ searchForm: searchQuery, config: appConfig });
  }, []);

  const changeMonth = (date: Date) => {
    const nextMonth = getMonth(date);
    if (searchedMonths.includes(nextMonth) || searchedMonths.length === 0) {
      return;
    }
    const updatedSearchForm = {
      ...searchQuery,
      flights: [
        {
          ...searchForm.flights[0],
          departureDate: format(date, "dd.MM.yyyy"),
        },
      ],
    };
    setSearchedMonths([...searchedMonths, nextMonth]);
    triggerForMonth({ searchForm: updatedSearchForm, config: appConfig });
  };

  useEffect(() => {
    if (data) {
      setAllItineraries({ ...allItineraries, ...data });
    }
  }, [data]);

  // Filter itineraries based on showYouthStudentItineraries preference
  useEffect(() => {
    const filtered: Record<string, IItinerary> = {};
    const minByMonth: Record<string, number> = {};

    // First, get all unique date keys (without _STU_YTH suffix)
    const dateKeys = new Set<string>();
    Object.keys(allItineraries).forEach((key) => {
      const dateKey = key.replace("_STU_YTH", "");
      dateKeys.add(dateKey);
    });

    // Process each date and find lowest price in the same loop
    dateKeys.forEach((dateKey) => {
      const regularItinerary = allItineraries[dateKey];
      const studentYouthItinerary = allItineraries[`${dateKey}_STU_YTH`];
      let selectedItinerary: IItinerary | null = null;

      if (showYouthStudentItineraries) {
        // When youth/student toggle is ON
        if (studentYouthItinerary && regularItinerary) {
          // Both exist - use student/youth only if cheaper
          if (
            studentYouthItinerary.totalFareAmounts.total <
            regularItinerary.totalFareAmounts.total
          ) {
            selectedItinerary = studentYouthItinerary;
          } else {
            selectedItinerary = regularItinerary;
          }
        } else if (studentYouthItinerary) {
          // Only student/youth exists
          selectedItinerary = studentYouthItinerary;
        } else if (regularItinerary) {
          // Only regular exists
          selectedItinerary = regularItinerary;
        }
      } else {
        // When youth/student toggle is OFF - always use regular if available
        if (regularItinerary) {
          selectedItinerary = regularItinerary;
        }
      }

      // Add to filtered results and update lowest price for the month
      if (selectedItinerary) {
        filtered[dateKey] = selectedItinerary;
        const price = selectedItinerary.totalFareAmounts.total;
        if (price > 0) {
          const monthKey = format(new Date(dateKey), "yyyy-MM");
          if (
            minByMonth[monthKey] === undefined ||
            price < minByMonth[monthKey]
          ) {
            minByMonth[monthKey] = price;
          }
        }
      }
    });

    setFilteredItineraries(filtered);
    setLowestPriceByMonth(minByMonth);
  }, [allItineraries, showYouthStudentItineraries]);

  return (
    <Box sx={{ ...sx.calendar } as SxProps} className="one-way-calendar">
      {/* An extra error popup for when the flights cannot be loaded */}
      {showErrorPopup && isError && !isFetching && (
        <CtaDialog
          {...translations.customResponseCodes["couldNotLoad"]}
          onClose={() => {
            setShowErrorPopup(false);
            navigateToSearchPage();
          }}
          ctaAction={() => {
            setShowErrorPopup(false);
            navigateToSearchPage();
          }}
          cta2Action={() => {
            setShowErrorPopup(false);
            navigateToSearchPage();
          }}
          icon={
            (translations.customResponseCodes["4007"].icon &&
              ResponseIconMap[translations.customResponseCodes["4007"].icon]) ||
            undefined
          }
        />
      )}

      <Container
        disableGutters
        sx={{
          ...sx.container,
          minHeight: "initial",
          maxHeight: "initial",
          height: "initial",
          px: "0!important",
        }}
      >
        <Box sx={{ ...oneWayStyles(theme) }}>
          {!isError && (
            <DayPicker
              className="Selectable"
              numberOfMonths={1}
              mode="single"
              weekStartsOn={1}
              defaultMonth={
                dateStringToDateObj(
                  searchQuery.flights[0].departureDate,
                ) as Date
              }
              disabled={{
                before: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000),
              }}
              fromDate={new Date()}
              showOutsideDays={false}
              locale={localeMap[locale as LocaleKey]}
              components={{
                HeadRow: HeadRow,
                Caption: (CaptionProps) => (
                  <Caption {...CaptionProps} changeMonth={changeMonth} />
                ),
                Day: (DayProps) => {
                  const monthKey = format(DayProps.displayMonth, "yyyy-MM");
                  const monthLowest = lowestPriceByMonth[monthKey] ?? null;
                  return (
                    <CalendarButtonOneWay
                      {...DayProps}
                      data={filteredItineraries}
                      isFetching={isFetching}
                      lowestPrice={monthLowest}
                      priceMemoryRef={priceMemoryRef}
                      {...props}
                    />
                  );
                },
              }}
            />
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default OneWayCalendar;
